"use client";

/**
 * `AgentChat.ModelSelector` — shadcn's Model Selector pattern
 * (https://ui.shadcn.com/docs/ai-elements/base/model-selector): one dropdown
 * trigger in the composer's `block-end` addon, a radio group per provider.
 * Reads the model out of `AgentChat.Settings` (throws without one).
 *
 * Model only, no effort menu: the engine takes no reasoning-effort setting.
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import { InputGroupButton } from "@repo/ui/components/ui/input-group";
import { ChevronDownIcon } from "lucide-react";
import { Fragment } from "react";
import { useAgentChatSettings } from "./settings";

export interface ModelOption {
  id: string;
  name: string;
  provider: string;
}

/**
 * The engine reports ids (`anthropic:claude-haiku-4-5`), not records. An id
 * with no provider prefix keeps its whole id as the name and groups under
 * "Model": guessing a vendor from a bare id would label a model wrongly.
 */
export function modelOptionFromId(id: string): ModelOption {
  const separator = id.indexOf(":");
  if (separator <= 0 || separator === id.length - 1) return { id, name: id, provider: "Model" };
  return { id, name: id.slice(separator + 1), provider: id.slice(0, separator) };
}

function groupByProvider(options: ModelOption[]): { name: string; models: ModelOption[] }[] {
  const providers = new Map<string, ModelOption[]>();
  for (const option of options) {
    providers.set(option.provider, [...(providers.get(option.provider) ?? []), option]);
  }
  return Array.from(providers, ([name, models]) => ({ name, models }));
}

export function AgentChatModelSelector() {
  const { model, onModelChange, models } = useAgentChatSettings();
  const options = models.map(modelOptionFromId);
  const providers = groupByProvider(options);
  const currentModel = options.find((item) => item.id === model);
  const currentName = currentModel?.name ?? "Select model";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<InputGroupButton size="sm" variant="outline" aria-label={`Model: ${currentName}`} />}
      >
        {currentName}
        <ChevronDownIcon />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuRadioGroup value={model} onValueChange={(value: string) => onModelChange(value)}>
          {providers.map((provider, index) => (
            <Fragment key={provider.name}>
              {index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuGroup>
                <DropdownMenuLabel>{provider.name}</DropdownMenuLabel>
                {provider.models.map((item) => (
                  <DropdownMenuRadioItem key={item.id} value={item.id} closeOnClick>
                    {item.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuGroup>
            </Fragment>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
